import {call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects'
import get from 'lodash/get'

import history from '../../history'

import * as checkoutActions from '../../actions/checkout'
import * as checkoutSelectors from '../../store/checkout'
import * as worldpayActions from '../../actions/worldpay'
import * as promoActions from '../../actions/promo'
import * as siteActions from '../../actions/site'
import * as userActions from '../../actions/user'
import * as userInteractionsActions from '../../actions/userInteractions'
import * as eventSelectors from '../../store/events'
import {forUserAndEvent} from '../login'
import {
  checkoutApiError,
  NO_EVENT,
  PRECHECKOUT_VALIDATION,
  PRECHECKOUT_VALIDATION_SERVER_ERROR,
} from '../../checkoutErrorCodes'
import {getFitGuarantee, getPrecheckoutValidation} from '../../api/api'
import {isBadResponse, isGoodResponse} from '../../api/statusCodes'
import {viewedModal} from '../../track/modals'
import * as URLS from '../../urls'

export function* validatePrecheckoutSuccessWorker() {
  const isValidOrder = yield select(checkoutSelectors.currentIsValidOrder)
  if (isValidOrder) {
    yield put(checkoutActions.orderIsValidPrecheckout())
  }
}

export function* validatePrecheckoutSuccessSaga() {
  yield takeLatest(checkoutActions.VALIDATE_PRECHECKOUT_SUCCESS, validatePrecheckoutSuccessWorker)
}

export function* validatePrecheckoutWorker() {
  try {
    yield put(checkoutActions.validatePrecheckoutPending())
    yield call(forUserAndEvent)
    const orderId = yield select(eventSelectors.currentOrderId)

    if (!orderId) {
      yield put(checkoutActions.validatePrecheckoutFailure({type: NO_EVENT}))
      return
    }

    const response = yield call(getPrecheckoutValidation, orderId)
    if (isGoodResponse(response.status)) {
      const responseData = get(response, 'json', {})
      const errorsList = get(responseData, 'errors', [PRECHECKOUT_VALIDATION_SERVER_ERROR])
      yield put(
        checkoutActions.validatePrecheckoutSuccess({
          ...responseData,
          errors: errorsList,
        }),
      )
    } else {
      throw checkoutApiError(PRECHECKOUT_VALIDATION, response.status)
    }
  } catch (e) {
    yield put(checkoutActions.validatePrecheckoutFailure(e))
  }
}

export function* validatePrecheckoutSaga() {
  yield takeLatest(checkoutActions.VALIDATE_PRECHECKOUT, validatePrecheckoutWorker)
}

export function* getFitGuaranteeWorker() {
  yield forUserAndEvent()

  try {
    const orderId = yield select(eventSelectors.currentOrderId)
    yield put(checkoutActions.getFitGuaranteePending())
    const {json, status} = yield call(getFitGuarantee, orderId)

    if (isBadResponse(status)) {
      throw new Error('Could not get fit guarantee response.')
    }

    yield put(checkoutActions.getFitGuaranteeSuccess(json))
  } catch (e) {
    yield put(checkoutActions.getFitGuaranteeFailure(e))
  }
}

export function* getFitGuaranteeSaga() {
  yield takeLatest(checkoutActions.GET_FIT_GUARANTEE, getFitGuaranteeWorker)
}

export function* validatePrecheckoutOnInitialPageLoadSaga() {
  yield take(siteActions.INITIAL_PAGE_LOAD)
  yield take(userActions.USER_AND_EVENT_RECEIVED)
  if (window.location.pathname === URLS.CHECKOUT) {
    yield call(validatePrecheckoutWorker)
  }
}

export function* displaySummaryLoaderOverlaySaga() {
  while (true) {
    yield take([
      promoActions.APPLY_PROMO_CODE_PENDING,
      checkoutActions.SAVE_SHIPPING_ADDRESS_PENDING,
      checkoutActions.GET_ORDER_SUMMARY_PENDING,
      checkoutActions.COMPLETE_CHECKOUT_PENDING,
    ])
    yield put(checkoutActions.showSummaryLoader())
    yield take([
      promoActions.APPLY_PROMO_CODE_SUCCESS,
      promoActions.APPLY_PROMO_CODE_FAILURE,
      checkoutActions.COMPLETE_CHECKOUT_SUCCESS,
      checkoutActions.COMPLETE_CHECKOUT_FAILURE,
      checkoutActions.SAVE_SHIPPING_ADDRESS_SUCCESS,
      checkoutActions.SAVE_SHIPPING_ADDRESS_FAILURE,
      checkoutActions.GET_ORDER_SUMMARY_SUCCESS,
      checkoutActions.GET_ORDER_SUMMARY_FAILURE,
    ])
    yield put(checkoutActions.hideSummaryLoader())
  }
}

export function* activateCheckoutPromptModalWorker() {
  const prompts = yield select(checkoutSelectors.currentCheckoutPrompts)

  if (prompts.length > 0) {
    yield put(userInteractionsActions.openCheckoutPromptModal())
    yield call(viewedModal, {message: 'No Fit Guarantee'})
  }
}

export function* activateCheckoutPromptModalSaga() {
  yield takeEvery(checkoutActions.GET_FIT_GUARANTEE_SUCCESS, activateCheckoutPromptModalWorker)
}

export function* getFitGuaranteeResponseWorker() {
  yield put(checkoutActions.getFitGuarantee())
}

export function* getFitGuaranteeResponseSaga() {
  yield takeEvery(checkoutActions.VALIDATE_PRECHECKOUT_SUCCESS, getFitGuaranteeResponseWorker)
}

export function* navigateToNextCheckoutStepWorker() {
  const currentStep = yield select(checkoutSelectors.currentStep)
  const steps = yield select(checkoutSelectors.steps)
  const stepIndex = steps.indexOf(currentStep)
  if (stepIndex < steps.length) {
    yield put(
      checkoutActions.navigateToCheckoutStep({
        currentStep: steps[stepIndex + 1],
        direction: 'forward',
      }),
    )
  }
}

export function* navigateToNextCheckoutStepSaga() {
  yield takeEvery(
    [
      checkoutActions.SAVE_PARTNER_INFO_SUCCESS,
      checkoutActions.SAVE_SHIPPING_ADDRESS_SUCCESS,
      worldpayActions.FETCH_PAYPAGE_REGISTRATION_ID_SUCCESS,
    ],
    navigateToNextCheckoutStepWorker,
  )
}

export function* activateCheckoutErrorModalWorker() {
  const shouldShowErrorModal = yield select(checkoutSelectors.shouldShowErrorModal)
  if (shouldShowErrorModal) {
    yield put(userInteractionsActions.openCheckoutErrorModal())
  }
}

export function* activateCheckoutErrorModalSaga() {
  yield takeEvery(
    [
      checkoutActions.VALIDATE_PRECHECKOUT_SUCCESS,
      checkoutActions.VALIDATE_PRECHECKOUT_FAILURE,
      checkoutActions.SAVE_BILLING_ADDRESS_FAILURE,
      checkoutActions.SAVE_PARTNER_INFO_FAILURE,
      checkoutActions.GET_ORDER_SUMMARY_FAILURE,
      checkoutActions.COMPLETE_CHECKOUT_FAILURE,
    ],
    activateCheckoutErrorModalWorker,
  )
}

export function* proceedToCheckoutWorker() {
  // Reset checkout step prior to starting checkout flow to catch users
  // that might've exited out of the checkout flow prior so we don't trigger
  // the totals endpoint unnecessarily from the ReviewStep component.
  yield put(checkoutActions.resetCheckoutStep())
  yield put(checkoutActions.validatePrecheckout())
  yield take(checkoutActions.VALIDATE_PRECHECKOUT_SUCCESS)
  const shouldProceedToCheckout = yield select(checkoutSelectors.shouldShowCheckoutContent)
  if (shouldProceedToCheckout) {
    yield call(history.push, URLS.CHECKOUT)
  }
}

export function* proceedToCheckoutSaga() {
  yield takeLatest(checkoutActions.PROCEED_TO_CHECKOUT, proceedToCheckoutWorker)
}

export function* acceptCheckoutPromptWorker() {
  yield put(userInteractionsActions.closeModal())
}

export function* acceptCheckoutPromptSaga() {
  yield takeLatest(checkoutActions.ACCEPT_CHECKOUT_PROMPT, acceptCheckoutPromptWorker)
}
